import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import ReunionAperturaNC from "./ReunionAperturaNC";
import ReconocimientoNC from "./ReconocimientoNC";
import InfoInstitucionalNC from "./InfoInstitucionalNC";
import EstadoResultadosNC from "./EstadoResultadosNC";
import EstadoAsistenciales from "./EstadoAsistencialesNC";
import EstadoApoyo from "./EstadoApoyoNC";
import TemasRelacionadosNC from "./TemasRelacionadosNC";
import EstadoSistemico from "./EstadoSistemicoNC";
import ReunionCierreNC from "./ReunionCierreNC";
import Tabla11 from "../Tabla11";
import Tabla11Obtener from "../../Informes/Tabla11Obtener";

/* =========================
    CONFIGURACIÓN
========================== */
const api = axios.create({
  baseURL:
    process.env.REACT_APP_SUA_API_INTERNA_URL ||
    "https://suadesarrollo.azurewebsites.net/api",
  auth: {
    username: process.env.REACT_APP_SUA_USUARIO,
    password: process.env.REACT_APP_SUA_CLAVE,
  },
});

/* =========================
    UTILIDADES
========================== */
/** Quita tabulaciones y espacios repetidos ("4.1.1.\tUbicacion" -> "4.1.1. Ubicacion"). */
const limpiar = (texto) =>
  String(texto ?? "")
    .replace(/\s+/g, " ")
    .trim();

/** Sección principal según el número inicial de la actividad ("4.1.2. ..." -> "4"). */
const seccionDe = (actividad) => {
  const coincidencia = limpiar(actividad).match(/^(\d+)\./);
  return coincidencia ? coincidencia[1] : "Otras";
};

/** Comparación sin mayúsculas ni tildes, para el buscador. */
const sinTildes = (texto) =>
  limpiar(texto)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const mensajeDeError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.mensaje ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  error?.message ||
  "Error desconocido";

/* =========================
    PANEL DE RESULTADO
========================== */
function ResultadoValidacion({ resultado, fecha, onCerrar }) {
  const [busqueda, setBusqueda] = useState("");

  const pendientes = resultado.pendientes ?? [];
  const total = resultado.totalActividades ?? 0;
  const asignadas = Math.max(total - pendientes.length, 0);
  const porcentaje = total > 0 ? Math.round((asignadas / total) * 100) : 0;

  /** Conteo de pendientes por motivo. */
  const motivos = useMemo(() => {
    const conteo = new Map();
    pendientes.forEach((p) => {
      const motivo = limpiar(p.motivo) || "Sin motivo";
      conteo.set(motivo, (conteo.get(motivo) ?? 0) + 1);
    });
    return [...conteo.entries()];
  }, [pendientes]);

  /** Pendientes filtrados por el buscador y agrupados por sección. */
  const grupos = useMemo(() => {
    const termino = sinTildes(busqueda);
    const mapa = new Map();

    pendientes
      .filter((p) => !termino || sinTildes(p.actividad).includes(termino))
      .forEach((p) => {
        const seccion = seccionDe(p.actividad);
        if (!mapa.has(seccion)) mapa.set(seccion, []);
        mapa.get(seccion).push(p);
      });

    return [...mapa.entries()].sort(
      ([a], [b]) => (Number(a) || Infinity) - (Number(b) || Infinity),
    );
  }, [pendientes, busqueda]);

  const visibles = grupos.reduce((suma, [, items]) => suma + items.length, 0);

  return (
    <Box sx={{ p: 2, textAlign: "left", borderBottom: "1px solid #e3e6f0" }}>
      <Alert
        severity={resultado.completa ? "success" : "warning"}
        onClose={onCerrar}
        sx={{ mb: 2 }}
      >
        <strong>
          {resultado.completa
            ? "Asignación completa."
            : "Asignación incompleta."}
        </strong>{" "}
        {resultado.mensaje}
        {fecha && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Validado a las {fecha.toLocaleTimeString("es-CO")}
          </Typography>
        )}
      </Alert>

      {total > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography variant="body2">
              {asignadas} de {total} actividades asignadas
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {porcentaje}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={porcentaje}
            color={resultado.completa ? "success" : "warning"}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {pendientes.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            {motivos.map(([motivo, cantidad]) => (
              <Chip
                key={motivo}
                size="small"
                label={`${motivo} (${cantidad})`}
              />
            ))}
          </Box>

          <TextField
            size="small"
            fullWidth
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar actividad pendiente…"
            sx={{ mb: 1.5 }}
            helperText={
              busqueda ? `${visibles} de ${pendientes.length} pendientes` : " "
            }
          />

          <Paper variant="outlined" sx={{ maxHeight: 420, overflow: "auto" }}>
            {visibles === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Ninguna actividad pendiente coincide con la búsqueda.
              </Typography>
            ) : (
              <List dense disablePadding>
                {grupos.map(([seccion, items]) => (
                  <li key={seccion}>
                    <ul style={{ padding: 0 }}>
                      <ListSubheader
                        sx={{
                          bgcolor: "#f8f9fc",
                          fontWeight: 700,
                          lineHeight: "36px",
                          color: "#4e73df",
                        }}
                      >
                        {seccion === "Otras" ? "Otras" : `Sección ${seccion}`} ·{" "}
                        {items.length} pendiente(s)
                      </ListSubheader>
                      {items.map((p) => (
                        <ListItem
                          key={p.actividadId}
                          divider
                          alignItems="flex-start"
                        >
                          <ListItemText
                            primary={limpiar(p.actividad)}
                            secondary={limpiar(p.motivo)}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { whiteSpace: "normal" },
                            }}
                          />
                        </ListItem>
                      ))}
                    </ul>
                  </li>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}

/* =========================
    COMPONENTE PRINCIPAL
========================== */
function AsignarTareasNC() {
  const { id, tipoSolicitud } = useParams();

  const [validando, setValidando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [fechaValidacion, setFechaValidacion] = useState(null);
  const [error, setError] = useState(null);

  /** GET validarAsignacionTareas/{id} */
  const validar = async () => {
    if (!id) return;

    setValidando(true);
    setError(null);

    try {
      const { data } = await api.get(`/validarAsignacionTareas/${id}`);
      console.log("validarAsignacionTareas:", data);
      setResultado(data);
      setFechaValidacion(new Date());
    } catch (err) {
      console.error("Error validando asignación", err);
      setResultado(null);
      setError(mensajeDeError(err));
    } finally {
      setValidando(false);
    }
  };

  return (
    <div className="test">
      <div>
        <div className="container">
          <div className="text-center">
            <div className="container-fluid">
              <div className="card shadow mb-4">
                <div
                  className="card-header py-3 d-flex flex-wrap justify-content-between align-items-center"
                  style={{ gap: 10 }}
                >
                  <h6 className="m-0 font-weight-bold text-primary">
                    Asignación de actividades · Solicitud {id} · Tipo{" "}
                    {tipoSolicitud}
                  </h6>

                  <Button
                    variant="contained"
                    color={
                      resultado
                        ? resultado.completa
                          ? "success"
                          : "warning"
                        : "primary"
                    }
                    onClick={validar}
                    disabled={validando || !id}
                    startIcon={
                      validando ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {validando
                      ? "Validando…"
                      : resultado
                        ? "Validar de nuevo"
                        : "Validar asignación"}
                  </Button>
                </div>

                {error && (
                  <Box sx={{ p: 2, textAlign: "left" }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                      No se pudo validar la asignación: {error}
                    </Alert>
                  </Box>
                )}

                {resultado && (
                  <ResultadoValidacion
                    resultado={resultado}
                    fecha={fechaValidacion}
                    onCerrar={() => setResultado(null)}
                  />
                )}

                <ReunionAperturaNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <ReconocimientoNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <InfoInstitucionalNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <EstadoResultadosNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <EstadoAsistenciales
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <EstadoApoyo idSolicitud={id} tipoSolicitudId={tipoSolicitud} />
                <TemasRelacionadosNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <EstadoSistemico
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <ReunionCierreNC
                  idSolicitud={id}
                  tipoSolicitudId={tipoSolicitud}
                />
                <Tabla11 idSolicitud={id} />
                <Tabla11Obtener idSolicitud={id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AsignarTareasNC;
